import React from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { Box, Stack, Typography } from '@mui/material';
import { createSx } from '@ui-lib/utils';
import Card from '@ui-lib/components/Card';
import Preloader from '@web-shared/elements/Preloader';
import useRequest from '@web-shared/hooks/useRequest';
import { showPublicBol } from '../../../../api';
import { parseEbolDetailsResponse } from '../../../../api/utils/shipmentEbolConverter';
import { Header } from '../../../../components/Header';
import { PhotoPreview } from '../../../../components/PhotoPreview';
import { PHOTO_TYPE } from '../../../../api/types/PhotoType';

import EbolDetailsFooter from '../BolDetailsFooter';
import { AddPodButton } from './components/AddPodButton';
import { EmptyState } from './components/EmptyState';
import BolStatus from './components/BolStatus';
import { DeliveryTokenProvider } from './components/DeliveryTokenContext';

const sx = createSx({
  root: {
    height: '100%',
  },
  content: {
    p: 2,
    margin: '0 auto',
    maxWidth: '1024px',
    width: '100%',
    height: '100%',
    overflowY: 'auto',
    boxSizing: 'border-box',
  },
  details: {
    display: 'grid',
    gap: 2,
    gridTemplateColumns: {
      xs: '1fr',
      sm: '1fr 1fr',
    },
  },
  destination: {
    gridColumn: {
      xs: '1 / -1',
    },
  }
});

interface Props {
  token: string;
  position: GeolocationPosition;
}

export const BolDetails = ({ token, position }: Props) => {
  const { t } = useTranslation();
  const { data, loading, setData } = useRequest(showPublicBol, { token });

  if (loading) {
    return <Preloader />;
  }

  const ebolDetails = parseEbolDetailsResponse(data);

  if (!ebolDetails) {
    return <EmptyState />;
  }

  const date = moment(ebolDetails.created_at).format('MMM D, YYYY');
  const isPodExist = ebolDetails.photos.some((e) => e.photo_type == PHOTO_TYPE.ACTUAL);

  return (
    <DeliveryTokenProvider
      token={token}
      userPosition={position}
      bolPosition={ebolDetails.destination_lat_lng}
    >
      <Stack sx={sx.root}>
        <Header />
        <Box sx={sx.content}>
          <Typography variant="h4" gutterBottom>
            {t('billOfLading')}
          </Typography>
          {ebolDetails.status && ebolDetails.updated_at &&
            <BolStatus status={ebolDetails.status} updated_at={ebolDetails.updated_at} />
          }
          <Card testID='bol-details-card'>
            <Box sx={sx.details}>
              <Stack>
                <Typography variant="overline">{t('referenceNumber')}</Typography>
                <Typography variant="body1">{ebolDetails.reference_number}</Typography>
              </Stack>
              <Stack>
                <Typography variant="overline">{t('dateAdded')}</Typography>
                <Typography variant="body1">{date}</Typography>
              </Stack>
              <Stack sx={sx.destination}>
                <Typography variant="overline">{t('destinationLocation')}</Typography>
                <Typography variant="body1">{ebolDetails.destination_title}</Typography>
              </Stack>
            </Box>
          </Card>
          {!isPodExist && (<AddPodButton
            setPhoto={(photo) => {
              setData(() => ({
                id: ebolDetails.id,
                ebol: {
                  ...ebolDetails,
                  photos: [
                    ...ebolDetails.photos,
                    { picture_url: photo, photo_type: PHOTO_TYPE.ACTUAL, id: Date.now() }
                  ],
                }
              }));
            }} />)}
          <PhotoPreview photos={ebolDetails.photos} createdAt={ebolDetails.created_at} />
        </Box>
        {!ebolDetails.status && isPodExist &&
          <EbolDetailsFooter
            ebolDetails={ebolDetails}
            onStatusChange={(details) => setData({
              id: ebolDetails.id,
              ebol: {
                ...ebolDetails,
                status: details.status,
                updated_at: details.updated_at,
                photos: details.photos,
              }
            })} />
        }
      </Stack >
    </DeliveryTokenProvider>
  );
};
